/**
 * LiveVibes — real-data-only landing widgets.
 *
 * Every component here shows ONLY genuine backend data. If a section has
 * nothing to display, it renders an honest empty-state (or hides itself)
 * instead of seeding fake numbers or fake usernames. Fresh-slate friendly.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSocial } from "@/lib/social";
import {
  Radio, Users, MessageSquare, Gamepad2, Crown, UserPlus,
} from "lucide-react";

// ================== COMPONENT: COMMUNITY STATS ROW ==================
export function CommunityStatsRow({ pulse }) {
  // Real numbers only — anything the backend doesn't report is shown as 0.
  const items = [
    { label: "Members Online",  value: pulse?.total_users        || 0, color: "#CCFF00", Icon: Users,         pulse: true  },
    { label: "Streams Live",    value: pulse?.live_streams       || 0, color: "#FF3366", Icon: Radio,         pulse: true  },
    { label: "Verified Creators", value: pulse?.verified_streamers || 0, color: "#A78BFA", Icon: Crown        },
    { label: "LFG Groups",      value: pulse?.lfg_open           || 0, color: "#22D3EE", Icon: UserPlus      },
    { label: "Tips Sent",       value: pulse?.tips_count         || 0, color: "#F59E0B", Icon: MessageSquare },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="community-stats">
      {items.map(it => (
        <div key={it.label} className="border border-white/10 rounded-sm p-3 bg-black/40 hover:border-white/25 hover:-translate-y-0.5 transition" data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g,"-")}`}>
          <div className="flex items-center gap-1.5 mb-1">
            {it.pulse && it.value > 0 && <span className="rec-dot" style={{ background: it.color, boxShadow: `0 0 6px ${it.color}` }} />}
            <it.Icon className="w-3 h-3" style={{ color: it.color }} />
            <span className="sc-mono text-[9px] uppercase tracking-widest text-zinc-500">{it.label}</span>
          </div>
          <div className="sc-heading text-xl font-black tabular-nums" style={{ color: it.color }}>
            {(it.value || 0).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

// ================== COMPONENT: LIVE ACTIVITY TICKER ==================
// Pulls the last few real signup + tip events from /api/landing/pulse.
// If there are no real events yet, renders a quiet empty-state — never fake data.
export function LiveActivityTicker() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/landing/pulse");
        if (!alive) return;
        const pulse = r.data || {};
        const rows = [];
        (pulse.recent_signups || []).forEach((s) => rows.push({
          id: `signup-${s.username}`,
          Icon: UserPlus,
          color: "#22D3EE",
          text: `@${s.username} joined MystiCast`,
          at: s.created_at,
        }));
        (pulse.recent_tips || []).forEach((t) => rows.push({
          id: `tip-${t.from_username}-${t.to_username}-${t.created_at}`,
          Icon: MessageSquare,
          color: "#F59E0B",
          text: `@${t.from_username} tipped ${t.amount} GC to @${t.to_username}`,
          at: t.created_at,
        }));
        rows.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
        setEvents(rows.slice(0, 6));
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 20000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div className="border border-white/10 rounded-md bg-black/50 overflow-hidden" data-testid="live-activity">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.02]">
        <Radio className="w-3.5 h-3.5 text-[#FF3366]" />
        <div className="sc-mono text-[10px] uppercase tracking-widest text-zinc-400">Live Activity</div>
      </div>
      {events.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="live-activity-empty">
          No activity yet — you could be the first entry here.
        </div>
      ) : (
        <div className="divide-y divide-white/5 max-h-64 overflow-hidden">
          {events.map((e) => (
            <div
              key={e.id}
              className="w-full flex items-center gap-3 px-3 py-2 text-xs"
              data-testid="activity-row"
            >
              <e.Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: e.color }} />
              <span className="text-zinc-300 truncate flex-1">{e.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================== COMPONENT: TRENDING CREATORS ROW ==================
// Only shows real live channels. Empty state when nobody is live.
export function TrendingCreators({ slots = 8 }) {
  const [creators, setCreators] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/channels?live_only=true");
        if (!alive) return;
        setCreators((r.data || []).slice(0, slots));
      } catch { /* silent */ }
      finally { if (alive) setLoaded(true); }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [slots]);

  return (
    <div data-testid="trending-creators">
      <div className="flex items-center gap-2 mb-3">
        <Crown className="w-4 h-4 text-[#FFD700]" />
        <div className="sc-label" style={{ color: "#FFD700" }}>Trending Creators</div>
      </div>
      {!loaded ? (
        <div className="h-24 rounded border border-white/10 bg-black/30 animate-pulse" />
      ) : creators.length === 0 ? (
        <div className="rounded border border-dashed border-white/15 p-6 text-center" data-testid="trending-creators-empty">
          <div className="text-sm text-zinc-300">No one live yet.</div>
          <div className="text-[11px] sc-mono text-zinc-500 mt-1">Your channel could be the first to trend here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {creators.map((c) => (
            <Link
              key={c.username}
              to={`/c/${c.username}/live`}
              className="relative flex flex-col items-center p-3 border border-white/10 rounded-sm bg-black/40 hover:border-[color:var(--sc-volt)] hover:-translate-y-0.5 transition-all group"
              data-testid={`trending-${c.username}`}
            >
              <div className="relative">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold border-2 group-hover:scale-105 transition-transform overflow-hidden"
                  style={{ borderColor: "#FF3366", background: "linear-gradient(135deg, #FF105333, transparent 70%)" }}
                >
                  {c.avatar_url ? (
                    <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (c.username || "?")[0].toUpperCase()
                  )}
                </div>
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 chip-neon pink text-[8px]" style={{ padding: "1px 6px" }}>
                  LIVE
                </span>
              </div>
              <div className="text-xs font-bold mt-2 truncate w-full text-center">{c.username}</div>
              <div className="text-[10px] text-zinc-500 truncate w-full text-center">{c.game_slug || c.category || "Live"}</div>
              {(c.viewer_count || 0) > 0 && (
                <div className="text-[10px] sc-mono text-[#FF3366] mt-1 flex items-center gap-1">
                  <span className="rec-dot" style={{ background: "#FF3366", width: 4, height: 4 }} />
                  {(c.viewer_count).toLocaleString()}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ================== COMPONENT: FRIEND STATUS RAIL ==================
// Uses the SocialProvider. Shows only real friends. Empty state when none.
export function FriendStatusRail() {
  const social = useSocial();
  const friends = social?.friends || [];

  return (
    <div className="border border-white/10 rounded-md bg-black/50" data-testid="friend-status">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.02]">
        <Users className="w-3.5 h-3.5 text-[color:var(--sc-cyan)]" />
        <div className="sc-mono text-[10px] uppercase tracking-widest text-zinc-400">Friends</div>
        <span className="sc-mono text-[9px] text-zinc-600 ml-auto">{friends.length} total</span>
      </div>
      {friends.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="friend-status-empty">
          No friends yet — add someone from the Friends panel.
        </div>
      ) : friends.slice(0, 6).map(f => (
        <div key={f.username} className="flex items-center gap-3 px-3 py-2 text-xs border-t border-white/5 hover:bg-white/[0.03] transition" data-testid={`friend-${f.username}`}>
          <div className="relative w-7 h-7 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-[10px] font-bold text-zinc-300 overflow-hidden">
            {f.avatar_url ? <img src={f.avatar_url} alt="" className="w-full h-full object-cover" /> : (f.username || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{f.username}</div>
            <div className="text-[10px] text-zinc-500 truncate">{f.display_name || ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ================== EXPORTED HELPER: NAV NOTIFICATION BADGE ==================
export function NotifBadge({ count }) {
  if (!count) return null;
  return (
    <span
      className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-black bg-[#FF3366] text-white flex items-center justify-center border-2 border-black anim-pulse"
      data-testid="notif-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
